import { api } from './api';

export type TipoResultadoIndicador = 'PORCENTAJE' | 'NUMERO' | 'MONEDA';

export type IndicadorEmpresa = {
  id: number;
  nombre: string;
  formula: string;
  formulaCalculo?: string;
  meta: number;
  frecuencia: Frecuencia;
  fuenteDatos: string;
  variables: Variable[];
  resultado: number;
  estado: string;
  observaciones: string | null;
  ultimaEvaluacion: string | null;
  tipoResultado: TipoResultadoIndicador;
};

export type Frecuencia = 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';

// Calcula los días hasta la próxima evaluación
export function calcularDiasProximaEvaluacion(indicador: IndicadorEmpresa): number | null {
  const { ultimaEvaluacion, frecuencia } = indicador;

  // Si nunca se ha evaluado, está vencido (retorna número muy negativo)
  if (!ultimaEvaluacion) {
    return -9999;
  }

  const mesesPorFrecuencia: Record<Frecuencia, number> = {
    MENSUAL: 1,
    TRIMESTRAL: 3,
    SEMESTRAL: 6,
    ANUAL: 12,
  };

  const mesesHastaProxima = mesesPorFrecuencia[frecuencia];

  // Fecha de última evaluación
  const fechaUltimaEval = new Date(ultimaEvaluacion);

  // Fecha límite para próxima evaluación
  const fechaLimite = new Date(fechaUltimaEval);
  fechaLimite.setMonth(fechaLimite.getMonth() + mesesHastaProxima);

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const diffTime = fechaLimite.getTime() - hoy.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

// Ordena indicadores por prioridad de evaluación (más urgentes primero)
export function ordenarPorPrioridad(indicadores: IndicadorEmpresa[]): IndicadorEmpresa[] {
  return [...indicadores].sort((a, b) => {
    const diasA = calcularDiasProximaEvaluacion(a) ?? Infinity;
    const diasB = calcularDiasProximaEvaluacion(b) ?? Infinity;
    return diasA - diasB; // Menor días = más urgente = primero
  });
}

export type Variable = {
  id: number;
  nombre: string;
  orden: number | null;
  valor?: number;
};

export type HistorialIndicador = {
  id: number;
  indicadorEmpresaId: number;
  mes: number;
  anio: number;
  valorMeta: number;
  valorActual: number;
  estado: string;
  fechaRegistro: Date;
};

export type IndicadorBase = {
  id: number;
  nombre: string;
  formula: string;
  frecuencia: Frecuencia;
  fuenteDatos: string;
  meta: number;
  variablesBase: IndicadorVariableBase[];
};

export type IndicadorVariableBase = {
  nombre: string; // "A", "B", "Ventas", etc.
  orden: number | null;
};

export type indicadoresEmpresaResponse = {
  success: boolean;
  indicadores: IndicadorEmpresa[];
};

export type indicadorEmpresaResponse = {
  success: boolean;
  indicador: IndicadorEmpresa;
};

export type HistorialIndicadoresResponse = {
  success: boolean;
  historial: HistorialIndicador[];
};

export type IndicadorBasesResponse = {
  success: boolean;
  baseIndicadores: IndicadorBase[];
};

export type ActualizarIndicadorResponse = {
  success: boolean;
  message: string;
};

export type ActualizarVariableResponse = {
  success: boolean;
  message: string;
};

export type listarVariablesResponse = {
  success: boolean;
  variables: Variable[];
};

export const indicadoresService = {
  async listarIndicadoresEmpresa(nit: string): Promise<indicadoresEmpresaResponse> {
    return await api.get<indicadoresEmpresaResponse>(`/empresas/${nit}/indicadores`);
  },

  async obtenerIndicadoresBase(nit: string): Promise<IndicadorBasesResponse> {
    return await api.get<IndicadorBasesResponse>(`/empresas/${nit}/indicadores/base`);
  },

  async obtenerHistorialIndicadores(nit: string): Promise<HistorialIndicadoresResponse> {
    return await api.get<HistorialIndicadoresResponse>(`/empresas/${nit}/indicadores/historial`);
  },

  async obtenerIndicadorEmpresaId(nit: string, indicadorId: number): Promise<indicadorEmpresaResponse> {
    return await api.get<indicadorEmpresaResponse>(`/empresas/${nit}/indicadores/${indicadorId}`);
  },

  async actualizarIndicadorEmpresa(nit: string, indicadorId: number, observaciones: string | null): Promise<ActualizarIndicadorResponse> {
    return await api.patch<ActualizarIndicadorResponse>(`/empresas/${nit}/indicadores/${indicadorId}`, { observaciones });
  },

  async listarVariablesIndicadorEmpresa(nit: string, indicadorId: number): Promise<listarVariablesResponse> {
    return await api.get<listarVariablesResponse>(`/empresas/${nit}/indicadores/${indicadorId}/variables`);
  },

  async actualizarVariableIndicador(nit: string, variableId: number, valor: number): Promise<ActualizarVariableResponse> {
    return await api.patch<ActualizarVariableResponse>(`/empresas/${nit}/indicadores/variables/${variableId}`, { valor });
  },

  async asignarIndicadorAEmpresa(nit: string, indicadorBaseId: number): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>(`/empresas/${nit}/indicadores`, { indicadorBaseId });
  },

  async eliminarIndicadorDeEmpresa(nit: string, indicadorId: number): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>(`/empresas/${nit}/indicadores/${indicadorId}`);
  },
};
